import { vec3 } from 'gl-matrix';
import { DICOMApprovalStatus } from '../dicom/dicomConstants';
import { isDerivedDose } from '../dose/derivedDose';
import { DoseMatrix } from '../patient/doseMatrix';
import { Image3D } from '../patient/image3d';
import { Patient } from '../patient/patient';
import { ApprovableRegistration } from '../patient/approvableRegistration';
import { Roi } from '../patient/roi';
import { StructureSet } from '../patient/structureset';
import {
    ApprovalView,
    AvailableStructureSetView,
    DoseDetailView,
    DoseListItemView,
    DoseProvenance,
    ImageListItemView,
    ImageReferenceView,
    ImageRegistrationsView,
    RegistrationView,
    RoiDetailView,
    RoiListItemView,
    StructureSetDetailView,
    StructureSetListItemView,
} from './views';

/** Manufacturer string that marks a dose as MVision-generated. */
const GENERATED_DOSE_MANUFACTURER = 'MVision AI';

function doseProvenance(dose: DoseMatrix): DoseProvenance {
    if (isDerivedDose(dose)) {
        return 'derived';
    }
    return dose.dicomDose.manufacturer === GENERATED_DOSE_MANUFACTURER
        ? 'predicted'
        : 'imported';
}

export function createRoiDetailView(roi: Roi): RoiDetailView {
    return {
        id: roi.id,
        roiNumber: roi.roiNumber,
        name: roi.name,
        color: { ...roi.color },
        approvalStatus: roi.approvalStatus,
        interpretedType: roi.interpretedType,
        category: roi.category,
        volumeCm3: roi.volumeMm3 != null ? roi.volumeMm3 / 1000 : null,
        contourCount: roi.contourCount,
        isEditable: !roi.isApproved,
        isEmpty: roi.isEmpty,
        isPointRoi: roi.isPointRoi,
    };
}

export function createRoiListItemView(roi: Roi): RoiListItemView {
    return {
        id: roi.id,
        roiNumber: roi.roiNumber,
        name: roi.name,
        category: roi.category,
        color: { ...roi.color },
        isApproved: roi.isApproved,
        isEmpty: roi.isEmpty,
    };
}

export function createStructureSetListItemView(
    ss: StructureSet
): StructureSetListItemView {
    return {
        id: ss.id,
        dicomUid: ss.dicomUid,
        displayName: ss.structureSetLabel,
        approvalStatus: ss.approvalStatus,
        roiCount: ss.rois.length,
        syncStatus: ss.syncStatus,
    };
}

export function createApprovalView(ss: StructureSet): ApprovalView {
    return {
        structureSetId: ss.id,
        approvalStatus: ss.approvalStatus,
        reviewerName: ss.reviewerName || null,
        canApprove: ss.approvalStatus !== DICOMApprovalStatus.APPROVED,
        canReject: ss.approvalStatus !== DICOMApprovalStatus.REJECTED,
        cascadingStatus: ss.getCascadingApprovalStatus(),
    };
}

/**
 * Detail view for the structure-set header and properties panel.
 * Denormalises image references so the UI does not need to navigate
 * ss.image directly.
 */
export function createStructureSetDetailView(
    ss: StructureSet
): StructureSetDetailView {
    const image = ss.image;
    return {
        id: ss.id,
        dicomUid: ss.dicomUid,
        displayName: ss.structureSetLabel,
        description: ss.structureSetDescription,
        approvalStatus: ss.approvalStatus,
        isApproved: ss.isApproved,
        reviewerName: ss.reviewerName,
        reviewDate: ss.reviewDate,
        reviewTime: ss.reviewTime,
        frameOfReferenceUid: ss.frameOfReferenceUid,
        numberOfStructures: ss.numberOfStructures,
        manufacturer: ss.manufacturer,
        manufacturerModelName: ss.manufacturerModelName,
        softwareVersions: ss.softwareVersions,
        generationModel: ss.generationModel,
        recordedOrigin: ss.recordedOrigin,
        structureGeneration: ss.rois.map((roi) => ({
            modelGenerated: roi.modelGenerated,
            contourEditedSinceGeneration: roi.contourEditedSinceGeneration,
        })),
        referencedSeriesUid: ss.dicomStructureSet.referencedSeriesUid,
        imageUid: image?.dicomUid ?? null,
        imageLabel: image?.seriesMetadata.seriesDescription ?? null,
        imageSeriesUid: image?.seriesUid ?? null,
        imageModality: image ? image.firstSlice.modality : null,
        imageSliceSpacingMm: image ? image.grid.getRes()[2] : null,
        imageSliceCount: image?.length ?? null,
    };
}

/**
 * AvailableStructureSetView factory for a structure set already
 * loaded into the patient aggregate.
 */
export function createAvailableStructureSetViewFromDomain(
    ss: StructureSet
): AvailableStructureSetView {
    return {
        sopInstanceUid: ss.dicomUid,
        displayName: ss.structureSetLabel,
        isLoaded: true,
        imageLabel: ss.image?.seriesMetadata.seriesDescription || undefined,
        imageSeriesUid: ss.image?.seriesUid || undefined,
        structureSetId: ss.id,
    };
}

/**
 * AvailableStructureSetView factory for a session-discoverable
 * structure set that has not yet been loaded. The display name and
 * SOP UID are taken from the session payload — no domain id exists
 * because there is no domain object yet.
 */
export function createAvailableStructureSetViewFromSession(args: {
    sopInstanceUid: string;
    displayName: string;
}): AvailableStructureSetView {
    return {
        sopInstanceUid: args.sopInstanceUid,
        displayName: args.displayName,
        isLoaded: false,
    };
}

/**
 * Pure field map from a {@link RigidRegistration} domain entity to its
 * read-side projection. Includes the domain {@link RegistrationView.id}.
 * Never throws and performs no engine reads.
 */
export function createRegistrationView(
    reg: ApprovableRegistration
): RegistrationView {
    return {
        id: reg.id,
        sopInstanceUid: reg.sopInstanceUid,
        contentLabel: reg.contentLabel,
        contentDescription: reg.contentDescription,
        contentCreatorName: reg.contentCreatorName,
        contentDate: reg.contentDate,
        sourceFrameOfReferenceUid: reg.sourceFrameOfReferenceUid,
        targetFrameOfReferenceUid: reg.targetFrameOfReferenceUid,
        approvalStatus: reg.approvalStatus,
        reviewerName: reg.reviewerName,
        isApproved: reg.isApproved,
        isNew: reg.isNew,
        registrationKind: reg.registrationKind,
    };
}

/**
 * Everything on the patient that depends on one image: structure sets
 * drawn on its series, registrations linking its frame of reference,
 * doses computed in its frame. Ordered by workflow (structure sets,
 * registrations, doses) and by insertion within each kind, so the list
 * is deterministic across renders.
 */
export function createImageReferencesView(
    patient: Patient,
    seriesUid: string,
    frameOfReferenceUid: string
): ImageReferenceView[] {
    const references: ImageReferenceView[] = [];
    for (const ss of patient.getStructureSetsForSeries(seriesUid)) {
        references.push({
            kind: 'structureSet',
            key: ss.dicomUid,
            label: ss.structureSetLabel,
            approvalStatus: ss.approvalStatus,
            roiCount: ss.numberOfStructures,
        });
    }
    for (const reg of patient.getRegistrationsForFrameOfReference(
        frameOfReferenceUid
    )) {
        references.push({
            kind: 'registration',
            key: reg.sopInstanceUid,
            label: reg.contentLabel || reg.contentDescription,
            approvalStatus: reg.approvalStatus,
        });
    }
    for (const dose of patient.getDosesForFrameOfReference(
        frameOfReferenceUid
    )) {
        references.push({
            kind: 'dose',
            key: dose.sopInstanceUid,
            label: dose.plan?.label ?? '',
            approvalStatus: null,
        });
    }
    return references;
}

/**
 * Pure field map from an {@link Image3D} to its list-item projection.
 * Never throws and performs no engine reads — in particular it does not
 * touch `pixelData`, which would force the whole volume to be assembled
 * just to render a list row.
 */
export function createImageListItemView(image: Image3D): ImageListItemView {
    return {
        id: image.id,
        seriesInstanceUid: image.seriesUid,
        studyInstanceUid: image.studyUid,
        frameOfReferenceUid: image.frameOfReferenceUid,
        label: image.seriesMetadata.seriesDescription,
        modality: image.seriesMetadata.modality,
        sliceCount: image.length,
        seriesDate: image.seriesMetadata.seriesDate,
        isNew: image.isNew,
        isExported: image.isExported,
    };
}

/**
 * Pure field map from a {@link DoseMatrix} to its detail projection.
 * Ported from the former `transformDoseMatrixToDoseData` transform.
 *
 * `maxDoseGy` is the **unscaled** clinical maximum. The display scale is a
 * UI setting and no longer lives on the entity, so it is applied downstream
 * by `buildDoseDisplayView`, which exposes the scaled value as a separate
 * `displayMaxDoseGy` field. DVH curve data is deliberately excluded — it is
 * computed on demand from the engine via `useDvh`, so dose metadata
 * re-renders stay decoupled from DVH chart re-renders.
 *
 * Never throws and performs no engine reads.
 */
export function createDoseDetailView(dose: DoseMatrix): DoseDetailView {
    const manufacturer = dose.dicomDose.manufacturer;
    return {
        id: dose.id,
        sopInstanceUid: dose.sopInstanceUid,
        // Unscaled — the display scale is a UI setting applied downstream.
        maxDoseGy: dose.doseMaxGy,
        type: dose.doseType,
        summationType: dose.doseSummationType,
        // Clone: Grid.getRes() returns its live buffer, so handing it out
        // would let a consumer mutate domain geometry through a value
        // advertised as an immutable projection.
        doseGridResolutionMm: vec3.clone(dose.grid.getRes()),
        planLabel: dose.plan?.label,
        provenance: doseProvenance(dose),
        manufacturer,
        manufacturerModelName: dose.dicomDose.manufacturerModelName,
        seriesDescription: dose.dicomDose.seriesDescription,
        doseComment: dose.dicomDose.doseComment,
        softwareVersions: dose.dicomDose.softwareVersions,
        targets: dose.targets,
    };
}

// The plan's label, else the dose's own series description.
export function doseLabel(dose: DoseMatrix): string | undefined {
    return dose.plan?.label ?? dose.dicomDose.seriesDescription;
}

/** Pure field map from a {@link DoseMatrix} to its list-item projection. */
export function createDoseListItemView(dose: DoseMatrix): DoseListItemView {
    return {
        id: dose.id,
        sopInstanceUid: dose.sopInstanceUid,
        imageUid: dose.structureSet?.image?.dicomUid,
        // A derived dose has no plan; its description is what names it.
        label: isDerivedDose(dose) ? doseLabel(dose) : dose.plan?.label,
        summationType: dose.doseSummationType,
        provenance: doseProvenance(dose),
        isNew: dose.isNew,
        isExported: dose.isExported,
    };
}

/**
 * Projects the registrations relevant to `primaryImage` grouped by their
 * secondary image's DICOM Series Instance UID.
 *
 * Replicates the grouping in RegistrationStore.createSnapshot:
 * `patient.getAllRegistrationsForPrimary(primaryImage)` returns one entry
 * per unique secondary image (deduplicated by series UID, primary
 * excluded), each carrying the rigid registrations linking it to the
 * primary. The grouping key is the secondary image's `seriesUid`.
 *
 * Pure; never throws; performs no engine reads.
 */
export function createImageRegistrationsView(
    patient: Patient,
    primaryImage: Image3D
): ImageRegistrationsView[] {
    return patient
        .getAllApprovableRegistrationsForPrimary(primaryImage)
        .map((imgReg) => ({
            imageSeriesUid: imgReg.image.seriesUid,
            registrations: imgReg.registrations.map(createRegistrationView),
        }));
}
